import * as React from 'react';
import { SpirometryAnalysisSetTypeEnum } from "../../../misc/api/ApiSpirometryAnalysisSetType"
import { Link } from "react-router-dom";
import { Column, Table } from "../../../Components/QueryTable";
import { MatchSortHeader } from "../../../Components/MatchSortHeader";
import { DateMatchSortHeader } from "../../../Components/DateMatchSortHeader";
import { WebPortalClient, FvcAnalysisSet } from "webportalclient";
import { formatDateTime } from "../../../misc/components/Formatters/Formatters";
import uniqBy from "lodash-es/uniqBy";
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';
import { HamburgerMenu } from '../../../misc/components/HamburgerMenu';
import { Authorization } from "../../../misc/components/Authorization";
import { getAnalysisSetsQcBlindingFilter, useQuery } from "../../../Components/Query";
import Condition from "../../../misc/components/Condition";
import { Permission, OverreadStatusIdEnum, User, ChallengeDecisionMaker, OverreadChallengeStatusEnum } from "../../../misc/api/apiTypes";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from '../../../misc/api/ApiSessionTypes';
import { CreateAnalysisBatchModal } from '../../QC/CreateAnalysisBatchModal';
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { AnalysisAcceptabilityStatus } from "../../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { AssignToUserInGroupModal } from '../../../misc/components/AssignToUserInGroupModal';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';
import { OverreadStatusHeader } from '../../../Components/OverreadStatusHeader';
import { AnalysisSetsTableAutomationId, getAnalysisSetsTableAutomationId } from '../../../misc/Helpers/AutomationIdHelper';
import { SessionChallengeStatus } from '../../../misc/components/SessionChallengeStatus';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { createFormatters } from "../../Shared/AnalysisHelpers";

export interface FvcAnalysisListTableProps {
}

export const FvcAnalysisSetListTable = (props: FvcAnalysisListTableProps) => {
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [subjectTypeOptions, setSubjectTypeOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analysisSetTypes, setAnalysisSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([])
    const { data, applyFilters, filters, refresh } = useQuery();
    const client = useWebPortalApiClient();
    const [legacyClient] = React.useState(useWebPortalLegacyClient())
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const [overreadEnabled, setOverreadEnabled] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);
    const [isAssignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);

    const handleReassign = async (analysisSetId: string, userName: string) => {

        const analysisSetForReassign = await client.client.getSpirometryAnalysisSet(analysisSetId);
        analysisSetForReassign.user = userName;

        await client.client.patchSpirometryAnalysisSet(analysisSetForReassign, analysisSetId);

        setReassignToUserModalVisible(false);
        refresh();
    }

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = uniqBy(sites.value, o => o.id).map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number,typeName,typeKey", `site/studyId eq ${study.id}`, "number");
            const options = uniqBy(subjects.value, o => o.id).map(s => ({
                value: s.id,
                label: s.number,
            }));
            const typeOptions = uniqBy(subjects.value, o => o.typeKey).map(s => ({
                value: s.typeKey,
                label: s.typeName,
            }));

            setSubjectOptions(options);
            setSubjectTypeOptions(typeOptions);
        };

        const getVisits = async () => {
            const visits = await client.client.getFvcSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getFvcSessions('name', `visit/subject/site/studyId eq ${study.id}`, 'name');
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getSpirometryAnalysisSets("analystUserId,user", undefined, undefined, undefined, undefined, undefined, "filter(user ne null)/groupby((analystUserId,user))");

            const options = uniqBy(analysts.value, o => o.analystUserId).filter(o => o.analystUserId).map(s => ({
                value: s.analystUserId,
                label: s.user,
            }));

            setAnalysts(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await client.client.getAnalysisSetTypes('externalCode,name', "contains(externalCode,'SAS')");
            const options = uniqBy(types.value, o => o.externalCode).map(s => ({
                value: s.externalCode,
                label: s.name,
            }));

            setAnalysisSetTypes(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        const applyStudySettings = async () => {
            const fvcSettings = await legacyClient.getStudyFvcSettings();
            setOverreadEnabled(fvcSettings.IsOverreadingAvailable);
        }

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalystSetTypes();
        getAnalysts();
        getOverreadChallengeStatuses();
        applyStudySettings();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const isQcAnalysisSet = (set: FvcAnalysisSet): boolean => set.type === SpirometryAnalysisSetTypeEnum.QC;

    const fvcFormatters = createFormatters<FvcAnalysisSet>(isQcAnalysisSet);
    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (analysisSet: FvcAnalysisSet) => {
        return (<>
            <AnalysisAcceptabilityStatus value={analysisSet.status} /><br />
            <SessionChallengeStatus challengeIsClosed={analysisSet.session.challengeIsClosed} challengeStatus={analysisSet.session.overreadChallengeStatus} analysisOverreadStatus={analysisSet.status} />
        </>);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const formatAnalysisSetType = (type: string) => {
        switch (type) {
            case SpirometryAnalysisSetTypeEnum.Overread:
                return "Overread";
            case SpirometryAnalysisSetTypeEnum.Site:
                return "Site";
            case SpirometryAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const refreshData = () => {
        setIsLoading(true);

        refresh();

        legacyClient.getStudyFvcSettings().then(stFvcS => {
            setOverreadEnabled(stFvcS && stFvcS.IsOverreadingAvailable);
            setIsLoading(false);
        });
    }

    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createFvcQC(sessionId, SpirometryAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refreshData();
    }
    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <FontAwesomeIcon icon={faCheck} className="icon" />
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <FontAwesomeIcon icon={faXmark} className="icon" />
                        There was an error. QC batch wasn't saved.
                    </div>
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const RenderHamburgerMenu = (analysisSet: FvcAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User, overreadEnabled: boolean) => {
        return (
            <HamburgerMenu contextMenuLeftAligned={true} automationId={`options_${analysisSet.id}`}>
                <Authorization permissionId={PermissionEnum.AccessSingleFvcSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <Condition test={() => (analysisSet.type !== SpirometryAnalysisSetTypeEnum.QC) || (analysisSet.type === SpirometryAnalysisSetTypeEnum.QC && currentUser.Username === analysisSet.user)}>
                        <li>
                            <Link data-automationid={`ViewLink_${analysisSet.id}`} to={{ pathname: `/Spirometry/Analysis/${analysisSet.id}/view`, state: { from: location.pathname + location.search }, } as unknown as Location}>
                            View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionIdsAll={[PermissionEnum.OverreadFvcSession]}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    {overreadEnabled && (!analysisSet || !analysisSet.user || analysisSet.user === currentUser.Username) ?
                        <Condition test={() => analysisSet !== null
                            && analysisSet.type !== SpirometryAnalysisSetTypeEnum.Site}>
                            <li>
                                <Link data-automationid={`OverreadLink_${analysisSet.id}`} to={{ pathname: `/Spirometry/Analysis/${analysisSet.id}/overread`, state: { from: location.pathname + location.search }, } as unknown as Location}>
                            Over-read</Link>
                            </li>
                        </Condition>
                        : null}
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateAQCFvcAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(analysisSet.sessionId); }}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadFvcSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread && (analysisSet.status === OverreadStatusIdEnum.Pending || analysisSet.status === OverreadStatusIdEnum.NotStarted)}>
                        <li>
                            <a data-automationid={`ReassignLink_${analysisSet.id}`} onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>
                                Reassign
                            </a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">FVC Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button
                            className="btn btn-primary"
                            data-automationid="fvc-analysis-sets-clear-filters"
                            onClick={clearAllFilters}>
                            Clear All Filters
                        </button>

                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQCFvcSelection}>
                            &nbsp;
                            <button className="btn btn-primary" onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                        </Authorization>
                    </div>
                </div>
            </div>
            {handleQcBatchSavingAlertMessage()}
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content" data-automationid="fvcAnalysisTable">
                            <Table data={data}>
                                <Column field="" header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Site" />} formatter={set => fvcFormatters.formatSiteId(set)} />
                                <Column field="" header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Subject" />} formatter={set => fvcFormatters.formatSubjectId(set)} />
                                <Column field="" header={<MatchSortHeader title={fieldDisplayText.SubjectType} field="session/visit/subject/typeKey" options={subjectTypeOptions} sortField="session/visit/subject/typeName" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="SubjectType" />} formatter={set => fvcFormatters.formatSubjectType(set)} />
                                <Column field="" header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Visit" />} formatter={set => fvcFormatters.formatVisitLabel(set)} />
                                <Column field="" header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" relatedFilter={getAnalysisSetsQcBlindingFilter()} automationId="Session" />} formatter={set => fvcFormatters.formatSessionLabel(set)} />
                                <Column field="type" header={<MatchSortHeader title="Analysis Set Type" field="type" options={analysisSetTypes} sortField="type" automationId="AnalysisSetType" />} formatter={type => formatAnalysisSetType(type)} />
                                <Column field="created" header={<DateMatchSortHeader title="Created At" field="created" sortField="created" automationId="CreatedAt" />} formatter={created => formatDateTime(created)} />
                                <Column field="" header={<DateMatchSortHeader title="Started At" field="started" sortField="started" automationId="StartedAt" />} formatter={set => formatDateTime(set.started)} />
                                <Column field="" header={<OverreadStatusHeader useStatusField={true} overreadAnalysisSetTypeValue={SpirometryAnalysisSetTypeEnum.Overread} automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`} orderField="status" challengeField="session/overreadChallengeStatus" overreadStatuses={statusesOptions} overreadChallengeStatuses={overreadChallengeStatuses} />} formatter={analysisSet => formatStatus(analysisSet)} />
                                {ViewOverreaderName() ?
                                    <Column field="user" header={<MatchSortHeader title="Over-reader" field="analystUserId" options={analysts} sortField="user" automationId="Overreader" />} formatter={analyst => formatAnalyst(analyst)} />
                                    : null 
                                }
                                <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id, currentUser, overreadEnabled)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Fvc} minimumTests={3} onSave={(result: boolean) => { setQcBatchSaved(result); refreshData(); }} onClose={() => setQCSelectionModelVisible(false)} /> : null}

            <AssignToUserInGroupModal show={isAssignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadFvcSession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadFvcSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user, userName) => { handleReassign(sessionIdToReassign, userName) }} />
        </>
    );

}

export default FvcAnalysisSetListTable;
